import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Icon from "@mdi/react";
import {
	mdiBellOutline,
	mdiCheckCircle,
	mdiAlertCircle,
	mdiKeyVariant,
	mdiTimerSandComplete,
	mdiCreditCardOutline,
	mdiOpenInNew,
	mdiCheckAll,
} from "@mdi/js";
import { Button, CircularProgress } from "@mui/material";
import { MainLayout, ListSidebar } from "layouts";
import { useNotifications } from "shared/hooks";
import { NotificationType, PublicNotificationDTO } from "shared/services";
import { clsx } from "@ismael1361/utils";
import styles from "./styles.module.scss";

const TYPE_ICONS: Record<string, string> = {
	task_completed: mdiCheckCircle,
	task_failed: mdiAlertCircle,
	oauth_required: mdiKeyVariant,
	rate_limit_hit: mdiTimerSandComplete,
	stripe_invoice_paid: mdiCreditCardOutline,
	stripe_payment_failed: mdiAlertCircle,
};

const TYPE_COLORS: Record<string, string> = {
	task_completed: "var(--mui-palette-success-main)",
	task_failed: "var(--mui-palette-error-main)",
	oauth_required: "var(--mui-palette-warning-main)",
	rate_limit_hit: "var(--mui-palette-warning-main)",
	stripe_invoice_paid: "var(--mui-palette-success-main)",
	stripe_payment_failed: "var(--mui-palette-error-main)",
};

const formatRelative = (iso: string): string => {
	const now = Date.now();
	const then = new Date(iso).getTime();
	const diffSec = Math.max(0, Math.floor((now - then) / 1000));
	if (diffSec < 60) return "agora";
	const diffMin = Math.floor(diffSec / 60);
	if (diffMin < 60) return `${diffMin} min`;
	const diffH = Math.floor(diffMin / 60);
	if (diffH < 24) return `${diffH}h`;
	const diffD = Math.floor(diffH / 24);
	if (diffD < 7) return `${diffD}d`;
	return new Date(iso).toLocaleDateString("pt-BR", { day: "2-digit", month: "short" });
};

const formatAbsolute = (iso: string): string =>
	new Date(iso).toLocaleString("pt-BR", { day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" });

interface ActionLink {
	label: string;
	href: string;
}

const actionLinkFor = (n: PublicNotificationDTO): ActionLink | null => {
	const data = n.data ?? {};
	switch (n.type) {
		case "task_completed":
		case "task_failed":
			return data.taskId ? { label: "Abrir tarefa", href: `/tasks?taskId=${data.taskId}` } : { label: "Ir para tarefas", href: "/tasks" };
		case "oauth_required":
			return data.contractedMcpId
				? { label: "Reconectar ferramenta", href: `/tools/${data.contractedMcpId}` }
				: { label: "Ir para ferramentas", href: "/tools" };
		case "rate_limit_hit":
			return { label: "Ver configurações", href: "/settings" };
		case "stripe_invoice_paid":
		case "stripe_payment_failed":
			return { label: "Abrir cobrança", href: "/settings" };
		default:
			return null;
	}
};

type Filter = "all" | "unread";

export const NotificationsPage: React.FC = () => {
	const navigate = useNavigate();
	const { notificationId } = useParams<{ notificationId?: string }>();
	const { items, loading, fetchList, markRead, markAllRead, unreadCount } = useNotifications();
	const [filter, setFilter] = useState<Filter>("all");
	const [search, setSearch] = useState("");

	useEffect(() => {
		void fetchList();
	}, [fetchList]);

	const sorted = useMemo(
		() => [...items].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()),
		[items],
	);

	const filtered = useMemo(() => {
		const term = search.trim().toLowerCase();
		return sorted.filter((n) => {
			if (filter === "unread" && n.read) return false;
			if (!term) return true;
			return n.title.toLowerCase().includes(term) || n.message.toLowerCase().includes(term);
		});
	}, [sorted, filter, search]);

	const selected = useMemo(() => {
		if (!notificationId) return null;
		return items.find((n) => n.id === notificationId) ?? null;
	}, [notificationId, items]);

	useEffect(() => {
		if (selected && !selected.read) void markRead(selected.id);
	}, [selected, markRead]);

	const handleSelect = (n: PublicNotificationDTO) => {
		navigate(`/notifications/${n.id}`);
	};

	const sidebar = (
		<ListSidebar
			header={
				<div className={styles.sidebarHeader}>
					<h2>Notificações</h2>
					{unreadCount > 0 && (
						<Button
							size="small"
							onClick={() => markAllRead()}
							startIcon={<Icon path={mdiCheckAll} size={0.7} />}
							sx={{ textTransform: "none", fontSize: "0.7rem", color: "var(--mui-palette-text-secondary)" }}
						>
							Marcar lidas
						</Button>
					)}
				</div>
			}
			searchPlaceholder="Buscar..."
			onSearch={setSearch}
		>
			<div className={styles.filters}>
				<button
					className={clsx(filter === "all" && styles.active)}
					onClick={() => setFilter("all")}
				>
					Todas {sorted.length > 0 ? `(${sorted.length})` : ""}
				</button>
				<button
					className={clsx(filter === "unread" && styles.active)}
					onClick={() => setFilter("unread")}
				>
					Não lidas {unreadCount > 0 ? `(${unreadCount})` : ""}
				</button>
			</div>

			{loading && filtered.length === 0 ? (
				<div className={styles.sidebarLoading}>
					<CircularProgress size={20} color="inherit" />
				</div>
			) : filtered.length === 0 ? (
				<div className={styles.sidebarEmpty}>
					<Icon path={mdiBellOutline} size={1.5} />
					<p>Nada por aqui.</p>
				</div>
			) : (
				filtered.map((n) => {
					const iconPath = TYPE_ICONS[n.type as NotificationType] ?? mdiBellOutline;
					const color = TYPE_COLORS[n.type as NotificationType] ?? "var(--mui-palette-text-secondary)";
					const isActive = selected?.id === n.id;
					return (
						<button
							key={n.id}
							className={clsx(styles.listItem, !n.read && styles.unread, isActive && styles.active)}
							onClick={() => handleSelect(n)}
						>
							<div className={styles.icon} style={{ color }}>
								<Icon path={iconPath} size={0.9} />
							</div>
							<div className={styles.body}>
								<div className={styles.row}>
									<span className={styles.title}>{n.title}</span>
									<span className={styles.time}>{formatRelative(n.createdAt)}</span>
								</div>
								<p className={styles.message}>{n.message}</p>
							</div>
							{!n.read && <span className={styles.unreadDot} />}
						</button>
					);
				})
			)}
		</ListSidebar>
	);

	const action = selected ? actionLinkFor(selected) : null;
	const iconPath = selected ? TYPE_ICONS[selected.type as NotificationType] ?? mdiBellOutline : mdiBellOutline;
	const iconColor = selected ? TYPE_COLORS[selected.type as NotificationType] ?? "var(--mui-palette-text-secondary)" : "var(--mui-palette-text-secondary)";
	const hasData = selected && selected.data && Object.keys(selected.data).length > 0;

	return (
		<MainLayout sidebar={sidebar}>
			<div className={styles.detailContainer}>
				{!selected ? (
					<div className={styles.detailEmpty}>
						<Icon path={mdiBellOutline} size={2} />
						<p>Selecione uma notificação para ver os detalhes.</p>
					</div>
				) : (
					<>
						<header className={styles.detailHeader}>
							<div className={styles.detailIcon} style={{ color: iconColor }}>
								<Icon path={iconPath} size={1.3} />
							</div>
							<div>
								<h1 className={styles.detailTitle}>{selected.title}</h1>
								<p className={styles.detailMeta}>{formatAbsolute(selected.createdAt)}</p>
							</div>
						</header>

						<div className={styles.detailBody}>{selected.message}</div>

						{hasData && (
							<div className={styles.detailDataBlock}>
								<h4>Detalhes</h4>
								<pre>{JSON.stringify(selected.data, null, 2)}</pre>
							</div>
						)}

						<div className={styles.detailActions}>
							{action && (
								<Button
									variant="contained"
									color="primary"
									startIcon={<Icon path={mdiOpenInNew} size={0.8} />}
									onClick={() => navigate(action.href)}
									disableElevation
								>
									{action.label}
								</Button>
							)}
						</div>
					</>
				)}
			</div>
		</MainLayout>
	);
};
